import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUserInfo } from '../store/userSlice';
import { AuthenticationService } from '../api/index.ts';
import './Login.css';

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 30_000;

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  const failedAttemptsRef = useRef(0);

  // Przechwycenie eventu instalacji PWA
  useEffect(() => {
    // Sprawdź czy apka jest już zainstalowana (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsAppInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const isLockedOut = useCallback(() => {
    if (!lockoutUntil) return false;
    if (Date.now() >= lockoutUntil) {
      setLockoutUntil(null);
      failedAttemptsRef.current = 0;
      return false;
    }
    return true;
  }, [lockoutUntil]);

  // Whitelist bezpiecznych ścieżek dla ?returnTo= — zapobiega open-redirect
  const isSafeReturnTo = (path) => typeof path === 'string' && path.startsWith('/preseason-game-invitations/');

  // Źródło prawdy: ?returnTo= w URL, fallback na location.state.from (legacy)
  const returnToParam = new URLSearchParams(location.search).get('returnTo');
  const safeReturnTo = isSafeReturnTo(returnToParam) ? returnToParam : null;
  const from = safeReturnTo || location.state?.from || '/';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLockedOut()) {
      const secondsLeft = Math.ceil((lockoutUntil - Date.now()) / 1000);
      setError(`Zbyt wiele nieudanych prób. Spróbuj ponownie za ${secondsLeft} s.`);
      return;
    }

    if (!formData.username || !formData.password) {
      setError('Wypełnij wszystkie pola');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Zaloguj się - endpoint zwraca { userId, userType }
      const loginResponse = await AuthenticationService.login({
        email: formData.username.trim(),
        password: formData.password
      });

      // Pobierz dodatkowe informacje o użytkowniku (w tym teamIds dla coacha)
      const userInfo = await AuthenticationService.getLoggedUserInfo();

      // Zapisz userId, username, name, userType, teams, sportsClubId, sportsClubName w Redux
      // Dla COACH: sportsClubId/sportsClubName sa teraz per-team w TeamJwtInfoDto
      // teamType i yearOfBirth sa teraz per-team w TeamJwtInfoDto
      failedAttemptsRef.current = 0;

      if (loginResponse && loginResponse.userId && loginResponse.userType) {
        dispatch(setUserInfo({
          userId: loginResponse.userId,
          username: loginResponse.username,
          name: userInfo.name || loginResponse.name,
          userType: loginResponse.userType,
          roles: userInfo.roles || loginResponse.roles || [],
          teams: userInfo.teams || [],
          sportsClubId: userInfo.sportsClubId,
          sportsClubName: userInfo.sportsClubName,
          logoUrl: userInfo.logoUrl,
          expressModeEnabled: userInfo.expressModeEnabled,
          expressModeThreshold: userInfo.expressModeThreshold,
          isApproved: userInfo.isApproved ?? loginResponse.isApproved
        }));

        // Sprawdź czy coach ma wymagane akcje dotyczące okresu przygotowawczego
        const preseasonAction = loginResponse.preseasonDefinitionActionRequired;
        if (loginResponse.userType === 'COACH' && preseasonAction?.actionRequired) {
          sessionStorage.setItem('preseasonTimesToDefine', JSON.stringify(preseasonAction.preseasonTimesToDefine));
          navigate('/settings', {
            replace: true,
            state: { preseasonTimesToDefine: preseasonAction.preseasonTimesToDefine }
          });
        } else if (from && from !== '/') {
          navigate(from, { replace: true });
        } else {
          navigate('/preseason-games-plans', { replace: true });
        }
      } else {
        setError('Błąd: brak userId lub userType w odpowiedzi');
      }
    } catch (loginError) {
      failedAttemptsRef.current += 1;
      if (failedAttemptsRef.current >= MAX_ATTEMPTS) {
        setLockoutUntil(Date.now() + LOCKOUT_DURATION_MS);
        setError(`Zbyt wiele nieudanych prób. Spróbuj ponownie za ${LOCKOUT_DURATION_MS / 1000} s.`);
      } else if (loginError.status === 401) {
        setError('Nieprawidłowy login lub hasło');
      } else if (loginError.status === 403 && loginError.body?.error === 'UserBlocked') {
        setError('Konto zostało zablokowane. Skontaktuj się z administratorem aby uzyskać więcej informacji.');
      } else if (loginError.status === 403) {
        setError('Konto nie zostało aktywowane lub brak dostępu');
      } else {
        setError('Wystąpił błąd podczas logowania');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon">🔐</div>
          <h1>Logowanie</h1>
          <p>Zaloguj się do swojego konta</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          {safeReturnTo && (
            <div className="info-banner" style={{ background: '#eef5ff', border: '1px solid #cfe1ff', color: '#1b3a6e', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 8 }}>
              Po zalogowaniu wrócisz do zaproszenia sparingowego.
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">
              <span className="label-icon">👤</span>
              Użytkownik
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Wprowadź login"
              autoComplete="username"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <span className="label-icon">🔑</span>
              Hasło
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Wprowadź hasło"
              autoComplete="current-password"
              maxLength={128}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-login" disabled={isLoading || isLockedOut()}>
            {isLoading ? 'Logowanie...' : 'Zaloguj się'}
          </button>

        </form>

        <div className="login-footer">
          <Link
            to={safeReturnTo ? `/register?returnTo=${encodeURIComponent(safeReturnTo)}` : '/register'}
            state={safeReturnTo ? undefined : { from }}
            className="register-link"
          >
            Załóż konto
          </Link>
          <Link to="/forgot-password" className="forgot-password-link">
            Zapomniałem hasła
          </Link>
        </div>

        {!isAppInstalled && (
          <div className="install-banner">
            {installPrompt ? (
              <button onClick={handleInstallClick} className="install-btn">
                Zainstaluj aplikację
              </button>
            ) : (
              <div className="install-hint">
                <span className="install-hint-text">Zainstaluj aplikację na telefonie</span>
                <span className="install-hint-steps">
                  Kliknij ikonę udostępniania w przeglądarce, a następnie „Dodaj do ekranu głównego"
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
